import React from 'react';

import { Button } from '@lightspeed/flame/Button';
import { Box, Flex } from '@lightspeed/flame/Core';
import { ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Text } from '@lightspeed/flame/Text';

import B2BMatrixContext from './B2BMatrixContext';
import B2BMatrixListing from './B2BMatrixListing';
import B2BMatrixPricingListing from './B2BMatrixPricingListing';
import { MatrixDialog } from './styles';
import { B2BMatrixContextItem, MatrixDialogProps } from './types';

export function B2BMatrixDialog({
  matrixItems,
  matrixID,
  onCancel,
  onConfirm,
  purchaseID,
}: MatrixDialogProps) {
  const [page, setPage] = React.useState(1);
  const [useDefaultCost, setUseDefaultCost] = React.useState(true);
  const [contextItems, setContextItems] = React.useState<B2BMatrixContextItem[]>(matrixItems);

  const selectedItemCount = contextItems.filter((item) => item.selected).length;

  const handleBack = () => {
    if (page > 1) {
      setPage(page - 1);
    } else {
      onCancel();
    }
  };

  const handleConfirm = () => {
    if (page === 2 || !purchaseID) {
      const selectedItems = contextItems.filter((item) => item.selected);
      onConfirm(selectedItems, matrixID, useDefaultCost);
    } else {
      setPage(page + 1);
    }
  };

  const selectAllItems = () => {
    setContextItems((prevItems) => {
      return prevItems.map((item) => ({
        ...item,
        selected: true,
      }));
    });
  };

  const selectNoneItems = () => {
    setContextItems((prevItems) => {
      return prevItems.map((item) => ({
        ...item,
        selected: item.previouslyImported,
      }));
    });
  };

  const updateItem = (index: number, func: (item: B2BMatrixContextItem) => void) => {
    setContextItems((prevItems) => {
      if (index < 0 || index >= prevItems.length) return prevItems;
      const newItems = [...prevItems];
      func(newItems[index]);
      return newItems;
    });
  };

  const toggleItemSelect = (index: number) => {
    updateItem(index, (item) => {
      item.selected = !item.selected;
    });
  };

  const setItemCost = (index: number, cost: number) => {
    updateItem(index, (item) => {
      item.cost = cost;
    });
  };

  const setItemPrice = (index: number, price: number) => {
    updateItem(index, (item) => {
      item.price = price;
    });
  };

  const toggleUseDefaultCost = () => {
    setUseDefaultCost((prev) => !prev);
  };

  let importButtonLabel = 'Import';
  if (purchaseID) importButtonLabel = 'Next';
  if (page === 2) importButtonLabel = 'Import and add items';

  return (
    <B2BMatrixContext.Provider
      value={{
        items: contextItems,
        selectAllItems,
        selectNoneItems,
        toggleItemSelect,
        setItemCost,
        setItemPrice,
        toggleUseDefaultCost,
        useDefaultCost,
      }}
    >
      <MatrixDialog isOpen={true} title="Create matrix">
        <ModalHeader onCloseClicked={onCancel}>Create matrix</ModalHeader>
        <ModalBody style={{ overflowY: 'scroll' }}>
          {page === 1 && <B2BMatrixListing />}
          {page === 2 && <B2BMatrixPricingListing />}
        </ModalBody>
        <ModalFooter>
          <Flex justifyContent="space-between" alignItems="center">
            <Text>
              <var>{selectedItemCount}</var> item{selectedItemCount !== 1 ? 's' : ''} will be
              imported
            </Text>
            <Box>
              <Button type="button" onClick={handleBack} mr={2}>
                {page === 1 ? 'Cancel' : 'Back'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                fill={true}
                onClick={handleConfirm}
                disabled={selectedItemCount === 0 && page === 1}
                data-test="import-confirm"
              >
                {importButtonLabel}
              </Button>
            </Box>
          </Flex>
        </ModalFooter>
      </MatrixDialog>
    </B2BMatrixContext.Provider>
  );
}
